/**
 * A module's Files, as the roadmaps draw them: starred ones pinned ("keep in focus"), the rest as
 * upload history, newest first. Uploads go through the OS file service; rows from before uploads
 * existed are named links and open in a new tab. Upload with the button or by dropping files on
 * the panel.
 *
 * The module keeps the rows and says what each row's meta line reads (`metaOf`) — size, who and
 * when, plus anything of its own, like the gate a product was in.
 */
import { Download, Star, Upload, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { fileUrl } from '../lib/files';
import { FileBadge } from './file-preview';
import { FilePickButton, useFileDrop } from './file-drop';

export interface ListedFile {
  id: string;
  name: string;
  /** The OS file; null for a named link from before uploads. */
  fileId: string | null;
  url: string;
  starred: boolean;
}

export interface FileActions<F extends ListedFile> {
  canEdit: boolean;
  /** The row's second line, e.g. "2.4 MB · Dev Admin · 12 Oct, 14:05". */
  metaOf: (file: F) => string;
  onPreview: (file: F) => void;
  onToggleStar: (file: F) => void;
  onRemove: (file: F) => void;
}

const iconButton =
  'grid size-7 shrink-0 place-items-center rounded-[6px] text-[hsl(var(--ns-boulder))] hover:bg-[hsl(var(--ns-pebble)/0.4)] hover:text-primary';

/**
 * One file: extension badge, name (opens the preview; a legacy link opens in a new tab), the meta
 * line, then star, download and remove. Starred rows sit on Unbleached.
 */
export function FileRow<F extends ListedFile>({
  file,
  canEdit,
  metaOf,
  onPreview,
  onToggleStar,
  onRemove,
}: { file: F } & FileActions<F>) {
  return (
    <li
      className={cn(
        'flex items-center gap-2.5 rounded-[10px] border py-2.5 pl-3 pr-2.5',
        file.starred
          ? 'border-[#E3D9C6] bg-[hsl(var(--ns-unbleached))]'
          : 'border-[hsl(var(--ns-acai)/0.1)] bg-background',
      )}
    >
      <FileBadge name={file.name} className={file.starred ? 'bg-background' : undefined} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {file.fileId === null ? (
          <a
            href={file.url}
            target="_blank"
            rel="noreferrer"
            className="truncate text-sm font-semibold text-primary hover:underline"
          >
            {file.name}
          </a>
        ) : (
          <button
            type="button"
            title="Preview"
            onClick={() => onPreview(file)}
            className="truncate text-left text-sm font-semibold hover:underline"
          >
            {file.name}
          </button>
        )}
        <span className="truncate text-xs text-[hsl(var(--ns-boulder))]">{metaOf(file)}</span>
      </div>
      {canEdit && (
        <button
          type="button"
          onClick={() => onToggleStar(file)}
          title={file.starred ? 'Unstar' : 'Star to keep in focus'}
          aria-label={file.starred ? `Unstar ${file.name}` : `Star ${file.name}`}
          className={cn(iconButton, file.starred && 'text-primary')}
        >
          <Star className={cn('size-[15px]', file.starred && 'fill-current')} strokeWidth={1.75} />
        </button>
      )}
      <a
        href={file.fileId === null ? file.url : fileUrl(file.fileId, { download: true })}
        {...(file.fileId === null ? { target: '_blank', rel: 'noreferrer' } : {})}
        title="Download"
        aria-label={`Download ${file.name}`}
        className={iconButton}
      >
        <Download className="size-3.5" strokeWidth={1.75} />
      </a>
      {canEdit && (
        <button
          type="button"
          onClick={() => onRemove(file)}
          title="Remove file"
          aria-label={`Remove ${file.name}`}
          className={iconButton}
        >
          <X className="size-3.5" strokeWidth={1.75} />
        </button>
      )}
    </li>
  );
}

const newestFirst = <F,>(files: readonly F[]) => [...files].reverse();

/** The starred files, under a "Starred" heading; nothing at all when none are. */
export function StarredFiles<F extends ListedFile>({
  files,
  ...actions
}: { files: readonly F[] } & FileActions<F>) {
  const starred = newestFirst(files).filter((file) => file.starred);
  if (starred.length === 0) return null;
  return (
    <section className="flex flex-col gap-2.5">
      <h4 className="flex items-center gap-1.5 pt-0.5 text-[13px] font-semibold">
        <Star className="size-[13px] fill-current" strokeWidth={1.75} />
        Starred
      </h4>
      <ul className="flex flex-col gap-2.5">
        {starred.map((file) => (
          <FileRow key={file.id} file={file} {...actions} />
        ))}
      </ul>
    </section>
  );
}

/**
 * The Files tab: upload history (everything not starred), and — with `withStarred` — the starred
 * files above it, for a page that doesn't pin them elsewhere.
 */
export function FilesPanel<F extends ListedFile>({
  files,
  withStarred = false,
  uploading,
  error,
  onUpload,
  className,
  ...actions
}: {
  files: readonly F[];
  withStarred?: boolean;
  /** How many uploads are in flight. */
  uploading: number;
  error: string;
  onUpload: (files: File[]) => void;
  className?: string;
} & FileActions<F>) {
  const history = newestFirst(files).filter((file) => !file.starred);
  const drop = useFileDrop(onUpload, actions.canEdit);
  return (
    <div
      {...drop.props}
      className={cn(
        'flex min-h-0 flex-col gap-2.5 rounded-xl outline-offset-[6px]',
        drop.over && 'outline-dashed outline-2 outline-primary',
        className,
      )}
    >
      {withStarred && <StarredFiles files={files} {...actions} />}
      <div className="flex items-center justify-between gap-2">
        <h4 className="flex items-baseline gap-2">
          <span className="text-[15px] font-semibold">Upload history</span>
          <span className="text-sm text-[hsl(var(--ns-boulder))]">{history.length}</span>
        </h4>
        {actions.canEdit && (
          <FilePickButton
            onFiles={onUpload}
            className="flex h-8 items-center gap-1.5 whitespace-nowrap rounded-[8px] border border-[hsl(var(--ns-acai)/0.14)] bg-background px-3 text-sm hover:border-[hsl(var(--ns-acai)/0.32)]"
          >
            <Upload className="size-3.5" strokeWidth={1.75} />
            Upload
          </FilePickButton>
        )}
      </div>
      {uploading > 0 && (
        <p className="text-[13px] text-[hsl(var(--ns-boulder))]">
          Uploading {uploading === 1 ? '1 file' : `${uploading} files`}…
        </p>
      )}
      {history.length > 0 ? (
        <ul className="flex flex-col gap-2.5 overflow-y-auto">
          {history.map((file) => (
            <FileRow key={file.id} file={file} {...actions} />
          ))}
        </ul>
      ) : (
        <div className="rounded-[10px] border-[1.5px] border-dashed border-[hsl(var(--ns-acai)/0.2)] p-[18px] text-center text-sm text-[hsl(var(--ns-boulder))]">
          {actions.canEdit ? 'Drop files here or upload' : 'No files yet.'}
        </div>
      )}
      {error && <p className="text-[13px] text-destructive">{error}</p>}
    </div>
  );
}
